import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
// Plans/2026-10-19-vorab-bestand-plan.md §2.1/§2.2: Leiter bucht Kartons einer WE-Position vorab in den
// Plattform-Bestand (Packstation, eingeraeumt). Nur Plattform — DR und `quantity` bleiben unberuehrt.
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useVorabDialogInfo, useVorabEinbuchen, useVorabKorrigieren, type VorabDialogInfo } from '@/api/vorab'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'

const zahl = (n: number) => n.toLocaleString('de-DE')
const STATUS: Record<'offen' | 'aufgegangen' | 'storniert', string> = {
  offen: 'offen — zählt zum Bestand, bis DR die Lieferung führt',
  aufgegangen: 'übernommen — DR führt die Lieferung',
  storniert: 'storniert',
}

/** Stueck aus Kartons wie im Backend: Kartons x Stueck je Karton, gedeckelt auf die Positionsmenge. */
export function vorabStueck(kartons: number, stueckJeKarton: number | null, maxStueck: number): number {
  return Math.min(kartons * (stueckJeKarton ?? 0), maxStueck)
}

function EinbuchenDialog({ open, onOpenChange, info, receiptId, sourceQty }: {
  open: boolean; onOpenChange: (o: boolean) => void; info: VorabDialogInfo; receiptId: number; sourceQty: number
}) {
  const [eingabe, setEingabe] = useState('')
  const einbuchen = useVorabEinbuchen(receiptId, info.receipt_item_id)
  useEffect(() => { if (open) setEingabe('') }, [open])
  const kartons = Number(eingabe)
  const gueltig = eingabe !== '' && Number.isInteger(kartons) && kartons >= 1 && kartons <= info.max_kartons
  const stueck = gueltig ? vorabStueck(kartons, info.stueck_je_karton, sourceQty) : 0
  const dr = info.bestand_dr ?? 0
  const vorabNachher = info.vorab_offen_kopf + stueck

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!gueltig || einbuchen.isPending) return
    try {
      const res = await einbuchen.mutateAsync({ cartons: kartons })
      toast.success(res.duplicate ? 'Vorab war schon gebucht' : `Vorab gebucht: ${zahl(res.stueck)} Stück (${res.cartons} Kartons)`)
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Vorab nicht gebucht')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Vorab in den Bestand</DialogTitle>
          <DialogDescription>
            <b><GrundartikelLink code={info.merchant_sku} /></b>: Kartons, die schon in der Packstation stehen, zählen sofort zum Bestand.
            DR bleibt unverändert, bis die Lieferung dort gebucht ist.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor={`vorab-kartons-${info.receipt_item_id}`}>Kartons in der Packstation (1–{info.max_kartons})</Label>
            <Input id={`vorab-kartons-${info.receipt_item_id}`} type="number" min={1} max={info.max_kartons} step={1}
              value={eingabe} onChange={(e) => setEingabe(e.target.value)} autoFocus className="w-28" />
            <div className="text-xs text-ink-2">1 Karton = {zahl(info.stueck_je_karton ?? 0)} Stück</div>
          </div>
          {gueltig && (
            <div className="rounded-md border border-line bg-panel-2 p-2 text-sm">
              Bestand danach: <b className="font-mono">{zahl(dr + vorabNachher)}</b>{' '}
              <span className="font-mono text-ink-2">({zahl(dr)} DR + {zahl(vorabNachher)} vorab)</span>
            </div>
          )}
          {!info.zaehlbasis && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Für diesen Artikel gibt es noch keine Fachzählung. „Gesamt“ in der Lagerort-Suche zeigt die Vorab-Stück erst
              nach der Erstzählung: Fach nach dem Einräumen in der Scan-App zählen (Handregal-Zählung).
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" disabled={!gueltig || einbuchen.isPending}>
              {einbuchen.isPending ? 'Buche…' : 'Vorab buchen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function KorrekturDialog({ open, onOpenChange, info, receiptId, maxStueck }: {
  open: boolean; onOpenChange: (o: boolean) => void; info: VorabDialogInfo; receiptId: number; maxStueck: number
}) {
  const vorher = info.vorab?.stueck ?? 0
  const [eingabe, setEingabe] = useState('')
  const [grund, setGrund] = useState('')
  const korrektur = useVorabKorrigieren(receiptId, info.receipt_item_id)
  useEffect(() => { if (open) { setEingabe(String(vorher)); setGrund('') } }, [open, vorher])
  const stueck = Number(eingabe)
  const gueltig = eingabe !== '' && Number.isInteger(stueck) && stueck >= 1 && stueck <= maxStueck
    && stueck !== vorher && grund.trim().length >= 3

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!gueltig || korrektur.isPending) return
    try {
      const res = await korrektur.mutateAsync({ stueck, grund: grund.trim() })
      toast.success(`Vorab korrigiert: ${zahl(res.stueck_vorher)} → ${zahl(res.stueck)} Stück`)
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Vorab nicht korrigiert')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Vorab korrigieren</DialogTitle>
          <DialogDescription>
            <b><GrundartikelLink code={info.merchant_sku} /></b>: bisher {zahl(vorher)} Stück vorab. Die Packstation-Menge wird mit korrigiert.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor={`vorab-stueck-${info.receipt_item_id}`}>Richtige Menge in Stück (1–{zahl(maxStueck)})</Label>
            <Input id={`vorab-stueck-${info.receipt_item_id}`} type="number" min={1} max={maxStueck} step={1}
              value={eingabe} onChange={(e) => setEingabe(e.target.value)} className="w-32" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`vorab-grund-${info.receipt_item_id}`}>Grund (Pflichtangabe)</Label>
            <Input id={`vorab-grund-${info.receipt_item_id}`} value={grund} onChange={(e) => setGrund(e.target.value)}
              placeholder="z. B. Kartons nachgezählt" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" disabled={!gueltig || korrektur.isPending}>
              {korrektur.isPending ? 'Speichere…' : 'Vorab korrigieren'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Vorab-Zeile einer WE-Position. Nur fuer Leiter, nur V2-Positionen, nur solange der WE offen ist. */
export function VorabBlock({ receiptId, receiptItemId, sourceQty, divertedCartons, enabled, neuErlaubt, rowBusy }: {
  receiptId: number; receiptItemId: number; sourceQty: number; divertedCartons: number
  enabled: boolean
  /** Neuer Vorab nur vor der ersten erfassten Palette und vor der Packstation-Bestaetigung. */
  neuErlaubt: boolean
  rowBusy: boolean
}) {
  const info = useVorabDialogInfo(receiptId, receiptItemId, enabled)
  const [einbuchenOffen, setEinbuchenOffen] = useState(false)
  const [korrekturOffen, setKorrekturOffen] = useState(false)
  const d = info.data
  if (!enabled || !d || (!d.vorab && (!d.moeglich || !neuErlaubt))) return null

  if (d.vorab) {
    const v = d.vorab
    // Obergrenze wie im Backend: eingeraeumte Kartons x Stueck je Karton, hoechstens die Positionsmenge.
    const maxStueck = vorabStueck(divertedCartons, d.stueck_je_karton, sourceQty)
    return (
      <div className="rounded-lg border border-line border-l-4 border-l-sky-500 bg-background p-3 text-sm flex flex-wrap items-center gap-2">
        <span className="flex-1 min-w-[220px]">
          Vorab <b className="font-mono">{zahl(v.stueck)} St.</b> <span className="rounded bg-blue-100 text-blue-900 px-2 py-1 text-xs" title={STATUS[v.status]}>{v.status === 'offen' ? 'Offen' : v.status === 'aufgegangen' ? 'Übernommen' : 'Storniert'}</span>
          <span className="text-ink-2"> · {new Date(v.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
            {v.created_by_name ? ` · ${v.created_by_name}` : ''}</span>
        </span>
        {v.status === 'offen' && d.sperre === null && (
          <Button size="sm" variant="outline" disabled={rowBusy} onClick={() => setKorrekturOffen(true)}>Vorab korrigieren…</Button>
        )}
        <KorrekturDialog open={korrekturOffen} onOpenChange={setKorrekturOffen} info={d} receiptId={receiptId} maxStueck={maxStueck} />
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Button variant="outline" disabled={rowBusy} onClick={() => setEinbuchenOffen(true)}>Vorab in den Bestand…</Button>
      <span className="text-xs text-muted">Kartons schon in der Packstation? Sie zählen dann sofort zum Bestand, bevor DR sie führt.</span>
      <EinbuchenDialog open={einbuchenOffen} onOpenChange={setEinbuchenOffen} info={d} receiptId={receiptId} sourceQty={sourceQty} />
    </div>
  )
}
